import { createSlice, createAsyncThunk, PayloadAction, createSelector } from '@reduxjs/toolkit';
import { getShadowbannedUids } from '../../services/firebase/admin.service';
import logger from '../../utils/logger';

/**
 * Shadowban Slice
 *
 * Manages the list of shadowbanned user UIDs for filtering content.
 * This slice is loaded on app startup for admin users and refreshed periodically.
 *
 * Behavior:
 * - For admins: Content from shadowbanned users is shown with a visual indicator
 * - For non-admins: Content from shadowbanned users is hidden entirely
 * - Shadowbanned users see their own content normally (they don't know they're shadowbanned)
 */

interface ShadowbanState {
  shadowbannedUids: string[];
  isLoading: boolean;
  error: string | null;
  lastFetched: number | null;
}

const initialState: ShadowbanState = {
  shadowbannedUids: [],
  isLoading: false,
  error: null,
  lastFetched: null
};

// Async thunk to fetch shadowbanned UIDs
// Only called for admin users
export const fetchShadowbannedUids = createAsyncThunk(
  'shadowban/fetchUids',
  async (_, { rejectWithValue }) => {
    try {
      const uids = await getShadowbannedUids();
      return uids;
    } catch (error) {
      logger.error('Error fetching shadowbanned UIDs:', error);
      // Don't reject - return empty array to avoid breaking the app
      return [];
    }
  }
);

const shadowbanSlice = createSlice({
  name: 'shadowban',
  initialState,
  reducers: {
    // Add a UID to the shadowbanned list (for real-time updates)
    addShadowbannedUid: (state, action: PayloadAction<string>) => {
      if (!state.shadowbannedUids.includes(action.payload)) {
        state.shadowbannedUids.push(action.payload);
      }
    },
    // Remove a UID from the shadowbanned list (for real-time updates)
    removeShadowbannedUid: (state, action: PayloadAction<string>) => {
      state.shadowbannedUids = state.shadowbannedUids.filter(uid => uid !== action.payload);
    },
    // Clear all shadowbanned UIDs (e.g., on logout)
    clearShadowbannedUids: (state) => {
      state.shadowbannedUids = [];
      state.lastFetched = null;
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchShadowbannedUids.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchShadowbannedUids.fulfilled, (state, action) => {
        state.isLoading = false;
        state.shadowbannedUids = action.payload;
        state.lastFetched = Date.now();
      })
      .addCase(fetchShadowbannedUids.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error.message || 'Failed to fetch shadowbanned users';
        // Keep existing list on error to avoid breaking feed
      });
  }
});

export const {
  addShadowbannedUid,
  removeShadowbannedUid,
  clearShadowbannedUids
} = shadowbanSlice.actions;

export default shadowbanSlice.reducer;

// Selectors
export const selectShadowbannedUids = (state: { shadowban: ShadowbanState }) =>
  state.shadowban.shadowbannedUids;

export const selectIsShadowbanned = (uid: string) => (state: { shadowban: ShadowbanState }) =>
  state.shadowban.shadowbannedUids.includes(uid);

export const selectShadowbanLastFetched = (state: { shadowban: ShadowbanState }) =>
  state.shadowban.lastFetched;

// Memoized selector that converts array to Set for O(1) lookups
// Use this in feed/comment filtering for better performance
export const selectShadowbannedUidsSet = createSelector(
  [selectShadowbannedUids],
  (uids): Set<string> => new Set(uids)
);
